import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Jute } from "../../..";
import { PerspectiveCamera } from "three";
import { useGet360VideoIdBySceneId, useGetCurrentSceneId } from "../../../hooks/general";
import { IEntityActionTypes, ISceneActions, ITransCatTypes, useActionStateStore, useDataStore, useSceneStore } from "../../../zustand";
import { IActionCategory, IPlaySoundAction, IRoot, ISceneComp, ITrackingTypes } from "../../r3f-components/component-data-structure";
import { useThree } from "@react-three/fiber";
import { useGetActiveTrackingType } from "../../hoc/hocCustomHooks";
import { presetAnimationController } from "../../PresetAnimationController";
import { getAllAnimationControllerOptions } from "../../../utils/action";
import { getAllOnloadModelAnimationPlayDataBySceneId } from "../../r3f-components/utils/actions";
import { hlsVideoCache } from "../../r3f-components/hooks";
import { return360VideoIdBySceneId } from "../../r3f-components/utils/content360";
import * as Sentry from "@sentry/react";

interface DeviceOrientationEventiOS extends DeviceOrientationEvent {
    requestPermission?: () => Promise<'granted' | 'denied' | 'default'>;
}

export const useIsDeviceOrientationEnabled = (isAlreadyEnabled: boolean, isIFrame: boolean) => {
    const [isDeviceOrientationEnabled, setIsDeviceOrientationEnabled] = useState(false);
    useLayoutEffect(() => {
        if (isIFrame) return;
        const requestPermission = (DeviceOrientationEvent as unknown as DeviceOrientationEventiOS).requestPermission;
        if (typeof requestPermission === 'undefined' || isAlreadyEnabled) {
            setIsDeviceOrientationEnabled(true)
        } else {
            Sentry.startSpan({ name: "device-orientation" }, () => {
                if (Jute.isLaunched) requestPermission().then((res) => {setIsDeviceOrientationEnabled(res === 'granted')})
                else Jute.onLaunch(() => requestPermission().then((res) => {setIsDeviceOrientationEnabled(res === 'granted')}));
            });
        } 
    }, [])
    return isDeviceOrientationEnabled;
}

export const useIsChangeTo360Scene = () => {
    const previousSceneId = useSceneStore(({ previousSceneId }) => previousSceneId);
    const componentsById = useDataStore(({ componentsById }) => componentsById);
    const currentSceneId = useGetCurrentSceneId();
    const previousScene = componentsById?.[previousSceneId || ''] as ISceneComp;
    const currentScene = componentsById?.[currentSceneId] as ISceneComp;

    return !!previousScene && previousScene.trackingType !== ITrackingTypes.content360 && currentScene.trackingType === ITrackingTypes.content360;
}

export const useIsChangeBetweenNon360Scenes = () => {
    const previousSceneId = useSceneStore(({ previousSceneId }) => previousSceneId);
    const componentsById = useDataStore(({ componentsById }) => componentsById);
    const currentSceneId = useGetCurrentSceneId();
    const previousScene = componentsById?.[previousSceneId || ''] as ISceneComp;
    const currentScene = componentsById?.[currentSceneId] as ISceneComp;

    return !!previousScene && previousScene.trackingType !== ITrackingTypes.content360 && currentScene.trackingType !== ITrackingTypes.content360;
}

export const useIsChangeFrom360Scene = () => {
    const previousSceneId = useSceneStore(({ previousSceneId }) => previousSceneId);
    const componentsById = useDataStore(({ componentsById }) => componentsById);
    const currentSceneId = useGetCurrentSceneId();
    const previousScene = componentsById?.[previousSceneId || ''] as ISceneComp;
    const currentScene = componentsById?.[currentSceneId] as ISceneComp;

    return !!previousScene && previousScene.trackingType === ITrackingTypes.content360 && currentScene.trackingType !== ITrackingTypes.content360;
}

export const useIsChangeBetween360Scenes = () => {
    const previousSceneId = useSceneStore(({ previousSceneId }) => previousSceneId);
    const componentsById = useDataStore(({ componentsById }) => componentsById);
    const currentSceneId = useGetCurrentSceneId();
    const previousScene = componentsById?.[previousSceneId || ''] as ISceneComp;
    const currentScene = componentsById?.[currentSceneId] as ISceneComp;

    return !!previousScene && previousScene.trackingType === ITrackingTypes.content360 && currentScene.trackingType === ITrackingTypes.content360;
}

export const useProjectHas360Scenes = (): boolean => {
    const componentsById = useDataStore(({ componentsById }) => componentsById);
	const rootId = useDataStore(({ rootId }) => rootId);
    const root = componentsById?.[rootId || ''] as IRoot | undefined;
    const num360Scenes = root?.children?.filter(id => (componentsById?.[id] as ISceneComp).trackingType === ITrackingTypes.content360)?.length;
    return (num360Scenes ?? 0) > 0;
}

export const useSwitchDefaultCameras = (camera360: PerspectiveCamera, cameraAR?: PerspectiveCamera) => {
    const { set } = useThree();

    const previousSceneId = useSceneStore(({ previousSceneId }) => previousSceneId);
    const componentsById = useDataStore(({ componentsById }) => componentsById);
	const rootId = useDataStore(({ rootId }) => rootId);

    const changeTo360 = useIsChangeTo360Scene();
    const changeFrom360 = useIsChangeFrom360Scene();
    const changeBetween360 = useIsChangeBetween360Scenes()

    const firstSceneId = (componentsById?.[rootId || ''] as IRoot)?.children[0];
    const firstScene = componentsById?.[firstSceneId] as ISceneComp;
    const previousScene = componentsById?.[previousSceneId || ''] as ISceneComp;
    const firstTime360Load = !previousScene && firstScene.trackingType === ITrackingTypes.content360;

    useLayoutEffect(() => {
        if (Jute.isPico || Jute.isQuest || Jute.isZapbox) return;
        if (firstTime360Load) {
            Jute.onFirstCameraFrame(() => {
                // We await the first camera frame as the zappar camera has set itself up
                // as the default camera by then and won't overwrite this change
                set(() => ({ camera: camera360 }));
            })
            return;
        }

        if (changeTo360 || changeBetween360) {
            set(() => ({ camera: camera360 }));            
            return;
        };

        if (changeFrom360) {
            if (cameraAR) set(() => ({ camera: cameraAR }));
        } 
    }, [changeFrom360, changeTo360, changeBetween360])
}

export const useSetup360SceneBehaviour = () => {
    const sceneDispatch = useSceneStore(({ dispatch }) => dispatch);
    const entityActionDispatch = useActionStateStore(({ dispatch }) => dispatch);
    const componentsById = useDataStore(({ componentsById }) => componentsById);

    const currentTrackingType = useGetActiveTrackingType();
	const currentSceneId = useGetCurrentSceneId();
    const currentSceneVideoId = return360VideoIdBySceneId(currentSceneId);


    const sceneComp = useMemo(() => componentsById?.[currentSceneId], [currentSceneId, componentsById]) as ISceneComp;
    const sceneOnLoadPlaySoundActions = useMemo(() => sceneComp?.actions?.onLoad?.filter(action => action.type === IActionCategory.playSound), [sceneComp]) as IPlaySoundAction[];

    useEffect(() => {
        if (currentTrackingType !== ITrackingTypes.content360) return;

        presetAnimationController
            .set({options: getAllAnimationControllerOptions(componentsById!).preset})
            .startAllOnloadAnimationsBySceneId(currentSceneId)

        entityActionDispatch?.({
            type: IEntityActionTypes.PLAY_MODEL_ANIMATIONS_BY_ID,
            payload: getAllOnloadModelAnimationPlayDataBySceneId(componentsById, currentSceneId)
        });

        sceneOnLoadPlaySoundActions?.forEach((action) => {
            let { stopMedia, loop } = action;
            if (stopMedia) {
                entityActionDispatch?.({
                    type: IEntityActionTypes.PAUSE_ALL_VIDEOS,
                });
                entityActionDispatch?.({
                    type: IEntityActionTypes.PAUSE_ALL_SCENE_AUDIO_BY_SCENE_ID,
                    payload: { id: currentSceneId },
                });
                entityActionDispatch?.({
                    type: IEntityActionTypes.PAUSE_BACKGROUND_AUDIO,
                });
            }
            entityActionDispatch?.({
                type: IEntityActionTypes.PLAY_ALL_SCENE_AUDIO_BY_SCENE_AND_ENTITY_ID,
                payload: { sceneId: currentSceneId, entityIds: [currentSceneVideoId] },
            });	
        })

        // start enter transition for current scene once scene has been placed
        sceneDispatch?.({
            type: ISceneActions.SET_TRANSITION_CATEGORY,
            transCatDict: {
                [currentSceneId]: ITransCatTypes.enter,
            },
        });
    }, [currentTrackingType, currentSceneId])
}

export const useSetupFirstSceneIs360Behaviour = (onShowContentFirstTime: () => unknown | undefined) => {
    const sceneDispatch = useSceneStore(({ dispatch }) => dispatch);
    const componentsById = useDataStore(({ componentsById }) => componentsById);
    const rootId = useDataStore(({ rootId }) => rootId);
    const firstSceneId = (componentsById?.[rootId || ''] as IRoot)?.children[0];
    const firstSceneVideo360Id = useGet360VideoIdBySceneId(firstSceneId);
    const firstScene = componentsById?.[firstSceneId] as ISceneComp;

    useLayoutEffect(() => {
        if (!firstScene || !firstSceneVideo360Id) return;
    
        sceneDispatch?.({
            type: ISceneActions.SET_PLACEMENT_MODE,
            placementMode: false,
        });

        if (firstScene.trackingType === ITrackingTypes.content360) {
            onShowContentFirstTime?.();
        }

        const hslData = hlsVideoCache[firstSceneVideo360Id];
        hslData?.[1]?.play()
    }, [firstScene, firstSceneVideo360Id])
}

export const useCallFirstSceneIs360CameraFrameCallback = () => {
    const [isLaunched, setIsLaunched] = useState(false);
    const previousSceneId = useSceneStore(({ previousSceneId }) => previousSceneId);
	const currentSceneId = useGetCurrentSceneId();
    const componentsById = useDataStore(({ componentsById }) => componentsById);
    const rootId = useDataStore(({ rootId }) => rootId);
    const firstSceneId = (componentsById?.[rootId || ''] as IRoot)?.children[0];
    const previousSceneIdRef = useRef(previousSceneId);
    const currentSceneIdRef = useRef(currentSceneId);
    const firstScene = componentsById?.[firstSceneId] as ISceneComp;

    useLayoutEffect(() => {
        if (Jute.isLaunched) setIsLaunched(true)
        else Jute.onLaunch(() => {
            setIsLaunched(true)
        });
    }, [])

    useEffect(() => {
        previousSceneIdRef.current = previousSceneId;
        currentSceneIdRef.current = currentSceneId;
    }, [previousSceneId, currentSceneId]);

    useEffect(() => {
        if (!isLaunched) return;
        const prevSceneId = previousSceneIdRef.current;
        const currSceneId = currentSceneIdRef.current;
        // If the first scene is a 360 content scene, start project immediately, otherwise wait first camera frame
        const isFirstTimeASceneIsShown = !prevSceneId && (currSceneId === firstSceneId);
        if (isLaunched && isFirstTimeASceneIsShown && firstScene.trackingType === ITrackingTypes.content360) {
            Jute.firstCameraFrame();
        };
    }, [isLaunched])
}



